import { Injectable, OnDestroy } from '@angular/core';
import { MarketplaceTokenRequest } from 'src/app/models/marketplace-token-request.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CloudVendor } from 'src/app/models/cloud-vendor.model';
import { CloudSubscriptionService } from 'src/app/cloud-subscription/services/cloud-subscription.service';
import { ServerDicObj } from 'src/app/models/server-dictionary-object';
import { takeUntil } from 'rxjs/operators';
import { Company } from 'src/app/models/company.model';
import Helper from 'src/app/Util/helper';
import { LocalStorageService } from '../../../../services/localstorage.service';
import { StorageKeys } from 'src/app/Util/storage-keys';
import { MarketPlaceOffer, PlanDetailResponse } from 'src/app/interfaces/plan.interface';
import { User } from 'src/app/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionCreateService implements OnDestroy {
  vendorName = '';
  cloudVendor: CloudVendor;
  cloudPlanResponse: MarketPlaceOffer;
  user: User;
  company: Company;
  wizardIndex = 0;
  unsubscribeSubject = new Subject();
  dataAsObservable = new BehaviorSubject(null);
  wizardIndexAsObservable = new BehaviorSubject(0);
  purchaseAcceptanceCheck = false;
  purchaseAcceptanceAsObservable = new BehaviorSubject(false);

  Helper = Helper;

  paygSelectedPlan: ServerDicObj;

  serverOnlyMap: Map<string, ServerDicObj>;
  clientServerMap: Map<string, ServerDicObj>;
  noOfClients: number;
  serverOnlyVmID: string;
  selectedServerOnlyLicenses = 0;
  selectedServerLicenses = 0;
  selectedClientLicenses = 0;
  is24x7Enabled = false;

  constructor(
    private localStorage: LocalStorageService,
    private subscriptionSvc: CloudSubscriptionService
  ) {
    this.serverOnlyMap = new Map<string, ServerDicObj>();
    this.paygSelectedPlan = new ServerDicObj();
    this.clientServerMap = new Map<string, ServerDicObj>();
    this.noOfClients = 0;
    this.serverOnlyVmID = '';
  }

  getPlanFromMarketPlaceToken(vendor: string, token: string) : Observable<PlanDetailResponse> {
    if (!token) return this.emitError("Token is missing or invalid.");
    if (!vendor) return this.emitError("Cloud Vendor is missing or invalid.");
    const cloudVendor = Helper.getVendor(vendor);
    if (!cloudVendor) return this.emitError("Unknown Cloud Vendor");
    this.saveCloudVendorToLocalStorage(cloudVendor);
    this.cloudVendor = cloudVendor;
    this.vendorName = vendor;
    const marketPlaceTokenRequest = this.createMarketplaceTokenRequest(token, vendor);

    this.subscriptionSvc.getPlanFromMarketPlaceToken(marketPlaceTokenRequest.token, marketPlaceTokenRequest.vendor).pipe(takeUntil(this.unsubscribeSubject)).subscribe({

      next: (cloudPlan) => {
        this.cloudPlanResponse = cloudPlan;
        this.saveCloudPlanToLocalStorage(cloudPlan);
        this.dataAsObservable.next({ cloudVendor, cloudPlan, hasError: false });
      },
      error: (error) => {
        this.dataAsObservable.error({ error, hasError: true });
      }
    });

    return this.dataAsObservable.asObservable();
  }

  getDimensionCosts(cloudplanId) {
    return this.subscriptionSvc.getDimensionCosts(cloudplanId, '');
  }

  setwizardIndexEmitter(index: number) {
    this.wizardIndex = index;
  }
  getwizardIndex(): Observable<number> {
    this.wizardIndexAsObservable.next(this.wizardIndex);
    return this.wizardIndexAsObservable.asObservable();
  }

  setNoOfClient(value: number) {
    this.noOfClients = value;
    this.setSelectedClientLicenses();
  }

  serverOnlyServers(key: string, server: ServerDicObj) {
    this.serverOnlyMap.set(key, server);
    this.setSelectedServerOnlyLicenses();
  }

  setServerOnlyServerMap(key: string, server: ServerDicObj) {
    this.serverOnlyMap = new Map<string, ServerDicObj>();
    this.serverOnlyMap.set(key, server);
    this.setSelectedServerOnlyLicenses();
  }

  clientServerServers(vmID: string, server: ServerDicObj) {
    this.clientServerMap.set(vmID, server);
    this.setSelectedServerLicenses();
  }

  setClientServerServerMap(vmID: string, server: ServerDicObj) {
    this.clientServerMap = new Map<string, ServerDicObj>();
    this.clientServerMap.set(vmID, server);
    this.setSelectedServerLicenses();
  }

  deleteClientServerServers(vmID: string) {
    this.clientServerMap.delete(vmID);
    this.setSelectedServerLicenses();
  }

  setSelectedServerOnlyLicenses() {
    if (this.serverOnlyMap.size > 0) {
      let noOfServerOnlyLicenses = 0;
      this.serverOnlyMap.forEach((x) => {
        noOfServerOnlyLicenses += x.noOfLicenses * x.noOfServers;
      });
      this.selectedServerOnlyLicenses = noOfServerOnlyLicenses;
    }
  }

  setSelectedClientLicenses() {
    this.selectedClientLicenses = this.noOfClients * 2;
  }

  setSelectedServerLicenses() {
    // if (this.clientServerMap.size > 0) {
    //   let noOfClientServerLicenses = 0;
    //   this.clientServerMap.forEach(x => {
    //     noOfClientServerLicenses += (x.noOfLicenses * x.noOfServers);
    //   });
    //   this.selectedServerLicenses = noOfClientServerLicenses;
    // }
    // else
    this.selectedServerLicenses = 0;
  }

  ngOnDestroy(): void {
    this.unsubscribeSubject.next(null);
    this.unsubscribeSubject.complete();
  }

  private createMarketplaceTokenRequest(
    token: string,
    vendorName: string
  ): MarketplaceTokenRequest {
    const req = new MarketplaceTokenRequest();
    req.token = vendorName === 'aws' ? escape(token) : token;
    req.vendor = vendorName;
    return req;
  }

  private saveCloudVendorToLocalStorage(cloudVendor: CloudVendor): void {
    this.localStorage.setItem(
      StorageKeys.CloudVendorId,
      cloudVendor.id.toString()
    );
    this.localStorage.setItem(StorageKeys.CloudVendor, cloudVendor.vendorName);
  }

  private saveCloudPlanToLocalStorage(cloudPlan: MarketPlaceOffer): void {
    this.localStorage.setItem(StorageKeys.CloudPlanProductName, cloudPlan.displayName);
    this.localStorage.setItem(StorageKeys.CloudPlanId, cloudPlan.offerId);
  }

  private emitError(errorMessage: string): Observable<PlanDetailResponse> {
    this.dataAsObservable.next({ error: errorMessage, hasError: true });
    return this.dataAsObservable.asObservable();
  }
}
